import React, { createContext, useReducer, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import useAutosave from "./util/useAutosave";
import { JOURNEY_CONTEXT_MAX_LENGTH, getJourneyDetailsValidation, normalizeActionItem, normalizeJourneyData } from './storage/actionItemModel';
import { updateActionItemStatusInDocs } from './storage/actionItemStatus';
import {
  chooseCloudJourneyVersion,
  chooseLocalJourneyVersion,
  deleteJourney,
  keepBothJourneyVersions,
  importJourneyBotRecordToLocal,
  importJourneyJsonToLocal,
  loadLocalGoalJourney,
  loadJourneyWorkingCopy,
  makeJourneyId,
  saveJourneyCloud,
  saveJourneyLocal,
} from './storage/journeyStorage';
import {
  getJourneyBotRecord,
  listJourneyBotJourneys,
  updateJourneyBotActionItem,
} from './api/journeyBotApi';
import { requiresEmailVerification } from './authVerification';
import { listLocalJourneys } from './storage/localJourneyStore';
import { SIGNED_IN_JOURNEY_LIMITS } from './config/journeyLimits';
import {
  clearGuestJourneyState,
  createGuestJourneyState,
  guestJourneyToBoardData,
  loadGuestJourneyState,
  saveGuestJourneyState,
} from './guestJourneySession';

const DEFAULT_GOAL_FOCUS = 'Own First Property';

const DEFAULT_GOALS = [
  { goal: DEFAULT_GOAL_FOCUS, journeyId: 'default' },
  { goal: 'Own First Car', journeyId: 'own-first-car' },
  { goal: 'Work at First Job', journeyId: 'work-at-first-job' },
  { goal: 'Entry to University', journeyId: 'entry-to-university' },
];

const mergeGoalsByJourneyId = (...goalLists) => {
  const goals = new Map();
  goalLists.flat().filter(Boolean).forEach((goal) => {
    if (goal?.journeyId) goals.set(goal.journeyId, { ...goals.get(goal.journeyId), ...goal });
  });
  return Array.from(goals.values());
};

const goalToJourneyId = (goalName) => (
  goalName === DEFAULT_GOAL_FOCUS ? 'default' : makeJourneyId(goalName)
);

const journeyBotLocalJourneyId = (journeyId) => `journeybot-${journeyId}`;

const makeClientItemId = () => (
  window.crypto?.randomUUID?.() || `journeybot-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const formatJourneyBotList = (label, values = []) => {
  const items = Array.isArray(values) ? values.filter(Boolean) : [];
  if (!items.length) {
    return '';
  }
  return `${label}:\n${items.map((item) => `- ${item}`).join('\n')}`;
};

const journeyBotRecordToBoardData = (record) => {
  const plan = record?.plan || {};
  const goalName = plan.goal || record?.journeyId || 'JourneyBot Journey';
  const actionItems = Array.isArray(plan.action_items) ? plan.action_items : [];
  const overviewText = [
    plan.context,
    formatJourneyBotList('Assumptions', plan.assumptions),
    formatJourneyBotList('Constraints', plan.constraints),
    formatJourneyBotList('Decisions', plan.decisions),
    formatJourneyBotList('Blockers', plan.blockers),
    formatJourneyBotList('Open questions', plan.open_questions),
    plan.next_action ? `Next action: ${plan.next_action}` : '',
    plan.source_summary ? `Summary: ${plan.source_summary}` : '',
  ].filter(Boolean).join('\n\n');

  const steps = [
    {
      step_id: 0,
      objective: goalName,
      objectiveDescription: plan.source_summary || plan.context || '',
      items: actionItems.map((item) => {
        const normalizedAction = normalizeActionItem(item);
        return normalizeActionItem({
          keyID: makeClientItemId(),
          value: [
            normalizedAction.title,
            normalizedAction.description,
            normalizedAction.status ? `Status: ${normalizedAction.status}` : '',
            normalizedAction.relevance ? `Relevance: ${normalizedAction.relevance}` : '',
            normalizedAction.user_note ? `Note: ${normalizedAction.user_note}` : '',
          ].filter(Boolean).join('\n\n'),
          status: normalizedAction.status,
          relevance: normalizedAction.relevance,
          journeyBotActionId: normalizedAction.id,
          journeyBotStatus: normalizedAction.status,
          journeyBotRelevance: normalizedAction.relevance,
          user_note: normalizedAction.user_note,
        });
      }),
    },
  ];

  if (overviewText) {
    steps.unshift({
      step_id: 0,
      objective: 'Journey context',
      objectiveDescription: goalName,
      items: [
        normalizeActionItem({
          keyID: makeClientItemId(),
          value: overviewText,
        }),
      ],
    });
  }

  return {
    fromTheme: plan.startState || goalName,
    startState: plan.startState || goalName,
    context: typeof plan.context === 'string' ? plan.context.slice(0, JOURNEY_CONTEXT_MAX_LENGTH) : '',
    endTheme: plan.endState || plan.next_action || goalName,
    endState: plan.endState || plan.next_action || goalName,
    steps: steps.map((step, index) => ({ ...step, step_id: index })),
  };
};

const initialState = {
  error: '',        // firebase loading state?
  dbState: '',      // loaded journey shown as the big graph
  documentType: 'document',
  dataLoaded: false,// firebase loaded indicator 
  userDocs: {},     // user journeys that have been loaded from firebase or created here
  journeyMeta: null,
  activeGoal: {
    goal: DEFAULT_GOAL_FOCUS,
    journeyId: 'default',
  },
  storageStatus: 'Cloud save available',
  cloudConflict: null,
  guestJourneyStatus: null,
  guestAttemptId: null,
  suppressAnonymousLoad: false,
  // this is used to get each sub item 





  userDocument: [],

  // THIS WAS JUST for the CHIPS and was meant only for testing
  lifeGoals: {
    goals: DEFAULT_GOALS,
  },
  journeyBotJourneys: [],
  journeyBotJourneyStatus: 'idle',

  // THIS IS HOW WE ENVISION IT TO LOOK LIKE
  loadedGoal: {
    goal: 'Own First Property',
    goalSteps: [
      { stepDesc: 'a step towards a goal', fromTheme: 'Financial', endTheme: 'Awareness', steps: [{ objective: 'SomeObjectivee', items: [{ value: 'dalveu', keyID: 'someKEy' }] }] },
      { stepDesc: 'a step towards a goal', fromTheme: 'Financial', endTheme: 'Awareness', steps: [{ objective: 'SomeObjectivee', items: [{ value: 'dalveu', keyID: 'someKEy' }] }] }
    ]
  },

  // regular users do not have access to templating
  // users create a goal
  // users create big steps towards a goal
  // this should be a significant step... try the reverse brainstorming method, what is preventing you to do this
  // each step can be broken down into key things to achieve (this is where it gets heavily personalised)
  // MVP : well deal with making it look better later
  // allow author to create a goal
  // allow author to weigh if a goal is a significant step or milestone
  // allow author to break each milestone into smaller goals with each one composed of another set of steps or ideas to try

  // owner occupied house (GOAL)
  // -> secure mortgage (GOAL STEP) - group tasks  (hard)
  //    -> save money  (long)  
  //    -> prepare credit score (med)
  //    -> prepare spending capacity for serviciablilty  (med)
  //    -> forecast per location for servicavility  (med)
  //    -> get pre-approval  (short)
  // -> find a location  (GOAL STEP) - group tasks (hard)
  //    -> start with ideal location
  //    -> explore surrounds
  //    -> determine what is importatn to you
  //    -> how long you planning to live there
  // -> buy a house or build a house
  // ACTIONS to check
  // TODO : will list render if there are no steps... think it will as long as there is a header
  // CONSIDERATIONS: clash in goal name
  //               : modified branch of goal




  // should have a userGoals []
  // each user goal then gets loaded into User Docs which I now use to display via GroupedTasks
  // requires that I have action to swtich between goals
  // must allow me to reprioritise goals 
  // must have export functioanlity 


  // Journey Graph Editor data
  // selected node
  // selected tree
  // 
  userPad: [],      // user drafts this is for loaded data into graph
  disableAutoSave: true,

  selectedNode: 0,
  selectedNodeContent: '',
  selectedStep: 0,
  isEmpty: true,
  showAlert: false,
  alertMessage: '',

  dispatcherAction: {}


};

const DataContext = createContext();

const cloneUserDocs = (userDocs) => JSON.parse(JSON.stringify(userDocs));

const removeJourneyBotGoals = (goals = []) => goals.filter((goal) => goal.source !== 'journeybot-api');

const markCloudSaveAvailable = (state, changes) => ({
  ...state,
  ...changes,
  storageStatus: state.guestJourneyStatus === 'preview'
    ? 'Guest preview · Not saved'
    : state.guestJourneyStatus === 'unsaved'
      ? 'Unsaved'
      : 'Cloud save available',
});

const guestPreviewPayload = (sessionState, userId = null) => {
  const journey = sessionState?.journey;
  if (!journey) return null;
  const journeyId = makeJourneyId(`guest-${sessionState.attemptId}`);
  const hasUsableBoardData = Array.isArray(sessionState.boardData?.steps) && sessionState.boardData.steps.length > 0;
  const data = normalizeJourneyData(hasUsableBoardData ? sessionState.boardData : guestJourneyToBoardData(journey));
  return {
    data,
    journeyMeta: {
      id: `${userId || 'guest'}:${journeyId}`,
      title: journey.goal,
      data,
      source: 'guest-ai',
      transient: true,
    },
    activeGoal: { goal: journey.goal, journeyId, source: 'guest-ai' },
    storageStatus: userId ? 'Unsaved' : 'Guest preview · Not saved',
    guestJourneyStatus: userId ? 'unsaved' : 'preview',
    guestAttemptId: sessionState.attemptId,
  };
};

const journeyDataHasChanged = (left, right) => (
  JSON.stringify(normalizeJourneyData(left)) !== JSON.stringify(normalizeJourneyData(right))
);

const logReducerAction = (action) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Reducer Start: ACTION IS ');
    console.log(action);
  }
};


// ACTION ON LIST ITEMS
// myState is the state of the graph
// action could be any of the following
// delete
// remove from the array 
// update
// replace content of array
// create
// insert at beginning of array

// pattern of updates are
// copy myState
// modify myState so that it contains the result of action

// ACTION ON THEME ITEMS
// create , this inserts to the right of the selected list (which i dont have a button for yet) 
// update , update lsit
// delete , remove entry, this should result inf removal of other cards too
// dispatch({ type: 'EDIT_ITEM', payload: { target: dataState.selectedNode, value: suggestion } });
//         ...myState, userDocs: updateGraphEntry(myState, action.payload), dispatcherAction: action
// NOTE it looks possible to group all list actions into one method 








// ACTION ON 



/*
A state reducer in React, when paired with Context, is a pattern used to manage and update the state of a component 
(or group of components) in a more structured and predictable way, especially in complex applications.

Key Points:
State Reducer: A reducer is a function that takes the current state and an action, and returns a new state. 
It is often used in conjunction with the useReducer hook to manage more complex state logic, similar to how Redux works.

React Context: React Context allows you to share state across components without 
having to explicitly pass props down through every level of the component tree.

what state will go here if any
- prevent a save when a previous save is in progress
- is this the place to perform queing
-  
*/

// note these initial states are for auth
// what state would be relevant here?
// what context to your data provider?
// maybe a time counter since last retry to prevent unecessary things (isnt this covered by useeffect)
// number of writes by the user to prevent further writes?
// permission to write ( should be in auth context)
// 
const dataReducer = (myState, action) => {
  logReducerAction(action);

  switch (action.type) {
    case 'CLEAR_DATA': {
      return {
        ...myState,
        userDocs: {},
        journeyMeta: null,
        activeGoal: {
          goal: DEFAULT_GOAL_FOCUS,
          journeyId: 'default',
        },
        storageStatus: 'Not signed in',
        cloudConflict: null,
        guestJourneyStatus: null,
        guestAttemptId: null,
        suppressAnonymousLoad: Boolean(action.payload?.suppressAnonymousLoad),
        dataLoaded: false,
        disableAutoSave: true,
        journeyBotJourneys: [],
        journeyBotJourneyStatus: 'idle',
        dispatcherAction: null,
        selectedNode: null,
        selectedNodeContent: null,
        lifeGoals: { goals: DEFAULT_GOALS },
      };
    }

    case 'LOAD_JOURNEYBOT_JOURNEYS': {
      const journeyBotJourneys = Array.isArray(action.payload?.journeys) ? action.payload.journeys : [];
      const existingGoals = removeJourneyBotGoals(myState.lifeGoals.goals);
      const existingJourneyIds = new Set(existingGoals.map((goal) => goal.journeyId));
      const journeyBotGoals = journeyBotJourneys.filter((journey) => !existingJourneyIds.has(journey.journeyId)).map((journey) => ({
        goal: journey.goal || journey.journeyId,
        journeyId: journeyBotLocalJourneyId(journey.journeyId),
        apiJourneyId: journey.journeyId,
        source: 'journeybot-api',
        sourceProvider: journey.sourceProvider,
        actionItemCount: journey.actionItemCount,
        updatedAt: journey.updatedAt,
      }));

      return {
        ...myState,
        journeyBotJourneys,
        journeyBotJourneyStatus: 'loaded',
        lifeGoals: {
          ...myState.lifeGoals,
          goals: [
            ...existingGoals,
            ...journeyBotGoals,
          ],
        },
      };
    }

    case 'JOURNEYBOT_JOURNEYS_UNAVAILABLE':
      return {
        ...myState,
        journeyBotJourneyStatus: 'unavailable',
        lifeGoals: {
          ...myState.lifeGoals,
          goals: removeJourneyBotGoals(myState.lifeGoals.goals),
        },
      };

    case 'LOAD_LOCAL_GOALS':
      {
        const localGoals = action.payload?.goals || [];
        const localJourneyIds = new Set(localGoals.map((goal) => goal.journeyId));
        const existingGoals = (myState.lifeGoals?.goals || []).filter((goal) => (
          goal.source !== 'journeybot-api' || !localJourneyIds.has(goal.apiJourneyId)
        ));
        return {
          ...myState,
          lifeGoals: {
            ...myState.lifeGoals,
            goals: mergeGoalsByJourneyId(existingGoals, localGoals),
          },
        };
      }

    case 'DELETE_GOAL_COMPLETE': {
      const nextState = {
        ...myState,
        lifeGoals: {
          ...myState.lifeGoals,
          goals: (myState.lifeGoals?.goals || []).filter(
            (goal) => goal.journeyId !== action.payload.deletedJourneyId,
          ),
        },
      };
      if (!action.payload.activeGoal) return nextState;
      return {
        ...nextState,
        dataLoaded: true,
        userDocs: action.payload.data,
        journeyMeta: action.payload.journeyMeta,
        activeGoal: action.payload.activeGoal,
        storageStatus: action.payload.storageStatus,
        cloudConflict: null,
        disableAutoSave: false,
      };
    }

    case 'LOAD_GUEST_PREVIEW':
      return {
        ...myState,
        dataLoaded: true,
        userDocs: action.payload.data,
        journeyMeta: action.payload.journeyMeta,
        activeGoal: action.payload.activeGoal,
        storageStatus: action.payload.storageStatus,
        cloudConflict: null,
        guestJourneyStatus: action.payload.guestJourneyStatus,
        guestAttemptId: action.payload.guestAttemptId,
        disableAutoSave: true,
        suppressAnonymousLoad: true,
      };

    case 'UPDATE_GUEST_DETAILS': {
      const nextDocs = normalizeJourneyData({ ...myState.userDocs, context: action.payload.context });
      return {
        ...myState,
        userDocs: nextDocs,
        journeyMeta: { ...myState.journeyMeta, title: action.payload.goalFocus, data: nextDocs },
        activeGoal: { ...myState.activeGoal, goal: action.payload.goalFocus },
        storageStatus: 'Unsaved',
      };
    }

    case 'GUEST_FIRST_SAVE_COMPLETE':
      return {
        ...myState,
        journeyMeta: action.payload.journeyMeta,
        storageStatus: 'Saved to cloud',
        cloudConflict: null,
        guestJourneyStatus: null,
        guestAttemptId: null,
        disableAutoSave: false,
      };

    case 'SELECT_GOAL': {
      return { ...myState, dispatcherAction: action };
    }


    case 'SELECT_ITEM':
      return { ...myState, selectedNode: action.payload.card, selectedNodeContent: action.payload.text };



    case 'LOAD':
      // {
      //initialState.dbState = action.payload;
      return { ...myState, userDocs: action.payload, dataLoaded: true };
    // }
    case 'UPDATE_LOCAL':
      // must how this deals with updates to objects
      // step 0 : { 'object_data' : 0}
      // step 1 : { 'object_data' : 1, 'another_data': 2}
      // will the object, in this case userPad have been updated properly
      return markCloudSaveAvailable(myState, { userDocs: action.payload, dataLoaded: true });

    case 'LOAD_USERDATA':
      {
        return {
          ...myState, dataLoaded: true, userDocs: action.payload, disableAutoSave: false
        }
      }

    case 'LOAD_WORKING_COPY':
      if (myState.guestJourneyStatus) return myState;
      return {
        ...myState,
        dataLoaded: true,
        userDocs: action.payload.data,
        journeyMeta: action.payload.journeyMeta,
        activeGoal: action.payload.activeGoal || myState.activeGoal,
        storageStatus: action.payload.storageStatus,
        cloudConflict: action.payload.cloudConflict || null,
        disableAutoSave: false
      };

    case 'LOAD_GOAL_WORKING_COPY':
      if (myState.guestJourneyStatus && action.payload?.activeGoal?.source !== 'guest-ai') return myState;
      return {
        ...myState,
        dataLoaded: true,
        userDocs: action.payload.data,
        journeyMeta: action.payload.journeyMeta,
        activeGoal: action.payload.activeGoal,
        storageStatus: action.payload.storageStatus,
        cloudConflict: null,
        disableAutoSave: false,
        dispatcherAction: action,
        lifeGoals: {
          ...myState.lifeGoals,
          goals: action.payload.activeGoal
            ? mergeGoalsByJourneyId(myState.lifeGoals?.goals || [], action.payload.activeGoal)
            : myState.lifeGoals?.goals || [],
        },
      };

    case 'LOCAL_SAVE_COMPLETE':
      return {
        ...myState,
        journeyMeta: action.payload.journeyMeta,
        storageStatus: action.payload.storageStatus,
        cloudConflict: null,
      };

    case 'CLOUD_SAVE_COMPLETE':
      return {
        ...myState,
        journeyMeta: action.payload.journeyMeta,
        storageStatus: 'Saved to cloud',
        cloudConflict: null,
      };


    //   dispatch({ type: 'EDIT_TASK', payload: {newName: objective, description: descDescription } });

    case 'NEW_TASK': {
      const myData = cloneUserDocs(myState.userDocs);
      if ((myData.steps || []).length >= SIGNED_IN_JOURNEY_LIMITS.stepsPerGoal) {
        return { ...myState, showAlert: true, alertMessage: `A journey can contain up to ${SIGNED_IN_JOURNEY_LIMITS.stepsPerGoal} steps.` };
      }
      // append at beginning of list or at the end of the list?
      // beginning of the list
      const newNAme = action.payload.newName;
      const newObjDesc = action.payload.description;

      // if there is an array
      if (myData.steps !== undefined) {
        myData.steps.push({ 'step_id': myData.steps.length, 'items': [], objective: newNAme, objectiveDescription: newObjDesc });

      } else {
        myData['steps'] = [{ 'step_id': 0, 'items': [], objective: newNAme, objectiveDescription: newObjDesc }];
      }

      return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });
    }

    case 'IMPORT_JOURNEYBOT_PLAN': {
      const myData = cloneUserDocs(myState.userDocs || {});
      const goalName = action.payload.goalName;
      const goalDescription = action.payload.goalDescription || action.payload.plan?.source_summary || '';
      const importedActions = action.payload.plan?.action_items || [];

      if (!Array.isArray(myData.steps)) {
        myData.steps = [];
      }

      let targetIndex = myData.steps.findIndex((step) => step.objective === goalName);
      if (targetIndex < 0) {
        targetIndex = myData.steps.length;
        myData.steps.push({
          step_id: targetIndex,
          items: [],
          objective: goalName,
          objectiveDescription: goalDescription,
        });
      }

      if (!Array.isArray(myData.steps[targetIndex].items)) {
        myData.steps[targetIndex].items = [];
      }

      const existingJourneyBotIds = new Set(
        myData.steps[targetIndex].items
          .map((item) => item.journeyBotActionId)
          .filter(Boolean)
      );

      const newItems = importedActions
        .filter((item) => !existingJourneyBotIds.has(item.id))
        .map((item) => {
          const normalizedAction = normalizeActionItem(item);
          return normalizeActionItem({
            keyID: crypto.randomUUID(),
            value: [
              normalizedAction.title,
              normalizedAction.description,
              normalizedAction.status ? `Status: ${normalizedAction.status}` : '',
              normalizedAction.relevance ? `Relevance: ${normalizedAction.relevance}` : '',
              normalizedAction.user_note ? `Note: ${normalizedAction.user_note}` : '',
            ].filter(Boolean).join('\n\n'),
            status: normalizedAction.status,
            relevance: normalizedAction.relevance,
            journeyBotActionId: normalizedAction.id,
            journeyBotStatus: normalizedAction.status,
            journeyBotRelevance: normalizedAction.relevance,
            user_note: normalizedAction.user_note,
          });
        });

      myData.steps[targetIndex].items.push(...newItems);

      return markCloudSaveAvailable(myState, {
        dataLoaded: true,
        userDocs: myData,
        dispatcherAction: action
      });
    }
    //        dispatch({ type: 'REMOVE_TASK', payload: { listroot: stepId } });

    case 'REMOVE_TASK': {
      const myData = cloneUserDocs(myState.userDocs);
      myData.steps.splice(action.payload.listroot, 1);
      return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });
    }


    //   dispatch({ type: 'EDIT_TASK', payload: { listroot: stepId, target: dataState.selectedNode, newName: objective, description: descDescription } });


    //   dispatch({ type: 'EDIT_TASK', payload: { listroot: stepId, target: dataState.selectedNode, newName: objective, description: descDescription } });

    case 'EDIT_TASK': {
      const myData = cloneUserDocs(myState.userDocs);
      const names = ['objective', 'objectiveDescription'];
      const index = action.payload.listroot;
      const newNAme = action.payload.newName;
      const newObjDesc = action.payload.description;


      // objective
      if (myData.steps[index].hasOwnProperty(names[0])) {
        myData.steps[index].objective = newNAme;
      } else {
        myData.steps[index][names[0]] = newNAme;
      }

      // objectiveDescription
      if (myData.steps[index].hasOwnProperty(names[1])) {
        myData.steps[index].objectiveDescription = newObjDesc;
      } else {
        myData.steps[index][names[1]] = newObjDesc;

      }

      return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });
    }

    case 'REORDER_GOAL': {
      const myData = cloneUserDocs(myState.userDocs);
      // action:reorder, payload = currentRoot , movement
      // myData = manageTasks(myState, action.payload);

      const [movedItem] = myData.steps.splice(action.payload.listroot, 1);
      myData.steps.splice(action.payload.listroot + action.payload.direction, 0, movedItem);

      return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });

    }

    case 'REORDER_TASK': {
      const myData = cloneUserDocs(myState.userDocs);
      // action:reorder, payload = currentRoot , movement
      // myData = manageTasks(myState, action.payload);

      const [movedItem] = myData.steps[action.payload.listroot].items.splice(action.payload.itemroot, 1);


      myData.steps[action.payload.listroot].items.splice(action.payload.itemroot + action.payload.direction, 0, movedItem);

      return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });

    }

    case 'TAKE_SUGGESTION': {
      const myData = cloneUserDocs(myState.userDocs);
      if ((myData.steps?.[action.payload.step_Id]?.items || []).length >= SIGNED_IN_JOURNEY_LIMITS.actionsPerStep) {
        return { ...myState, showAlert: true, alertMessage: `A step can contain up to ${SIGNED_IN_JOURNEY_LIMITS.actionsPerStep} actions.` };
      }
      // User clicks add new step
      // Input will be location represented as (stepId, index)
      // and text to put simply as text
      // { step_id: X, index:0, value: XYZ}
      //  ...userPad
      // var tempPad = userPad;
      // lets try with an array first
      const merged = normalizeActionItem({ ...action.payload, ...{ keyID: crypto.randomUUID() } });

      myData.steps[action.payload.step_Id].items.push(merged);
      return markCloudSaveAvailable(myState, { dataLoaded: true, userDocs: myData, dispatcherAction: action });
    }
    case 'EDIT_ITEM': {
      const myData = cloneUserDocs(myState.userDocs);

      const targetIndex = myData.steps[action.payload.step_Id].items.findIndex(item => item.keyID === action.payload.target);
      if (targetIndex >= 0) {
        myData.steps[action.payload.step_Id].items[targetIndex].value = action.payload.value;
      }
      return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });
    }


    case 'REMOVE_SUGGESTION':
      {
        const myData = cloneUserDocs(myState.userDocs);
        const newArray = myData.steps[action.payload.step_Id].items.filter((item) => {
          return item.keyID !== action.payload.keyId;
        });

        myData.steps[action.payload.step_Id].items = newArray;

        return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });
      }

    case 'SET_ACTION_ITEM_STATUS':
      return {
        ...myState,
        dataLoaded: true,
        userDocs: updateActionItemStatusInDocs(myState.userDocs, action.payload),
        dispatcherAction: action,
      };

    case 'JOURNEY_DETAILS_SAVE_COMPLETE': {
      const title = action.payload.title;
      return {
        ...myState,
        userDocs: action.payload.journeyMeta.data,
        journeyMeta: action.payload.journeyMeta,
        activeGoal: { ...myState.activeGoal, goal: title },
        lifeGoals: {
          ...myState.lifeGoals,
          goals: (myState.lifeGoals?.goals || []).map((goal) => (
            goal.journeyId === myState.activeGoal?.journeyId ? { ...goal, goal: title } : goal
          )),
        },
        storageStatus: 'Saved locally',
        cloudConflict: null,
      };
    }

    case 'SHOW_ALERT':
      return {
        ...myState, showAlert: true, alertMessage: action.payload.message
      }

    case 'EDITABLE_TITLE':
      //update teh 
      //  find where we are in the graph, get the ID of the current item
      //  if it doesnt have one, this should never be the case


      return {
        ...myState, dispatcherAction: action
      }

    case 'EDITABLE_TITLE_FROM':
      //update teh 
      //  find where we are in the graph, get the ID of the current item
      //  if it doesnt have one, this should never be the case
      {
        const myData = cloneUserDocs(myState.userDocs);
        myData.fromTheme = action.payload;
        myData.startState = action.payload;

        return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });
      }

    case 'EDITABLE_TITLE_END':
      //update teh 
      //  find where we are in the graph, get the ID of the current item
      //  if it doesnt have one, this should never be the case
      {
        const myData = cloneUserDocs(myState.userDocs);
        myData.endTheme = action.payload;
        myData.endState = action.payload;


        return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });
      }

    case 'UPDATE_JOURNEY_FRAMING':
      {
        const myData = normalizeJourneyData(cloneUserDocs(myState.userDocs));
        if (Object.prototype.hasOwnProperty.call(action.payload, 'startState')) {
          myData.startState = action.payload.startState;
          myData.fromTheme = action.payload.startState;
        }
        if (Object.prototype.hasOwnProperty.call(action.payload, 'context')) {
          myData.context = action.payload.context;
        }
        if (Object.prototype.hasOwnProperty.call(action.payload, 'endState')) {
          myData.endState = action.payload.endState;
          myData.endTheme = action.payload.endState;
        }

        return markCloudSaveAvailable(myState, { userDocs: myData, dispatcherAction: action });
      }


    default:
      //  { 
      if (process.env.NODE_ENV !== 'production') {
        console.log('DEFAULT dispatch');
      }
      return myState;
    //  }
  }
};

/*
This is where you put in functionality


endpoints to do
() get journey for user only
() get suggestions (this is where a shared model can help) for next steps
() save edits by user ()




*/

export const DataProvider = ({ children }) => {
  const { authState } = useAuth();
  const initialGuestSession = useRef(loadGuestJourneyState());
  const initialGuestPayload = guestPreviewPayload(initialGuestSession.current, authState.user?.uid || null);
  const [dataState, dispatch] = useReducer(dataReducer, initialGuestPayload
    ? { ...initialState, ...initialGuestPayload, dataLoaded: true, disableAutoSave: true, suppressAnonymousLoad: true }
    : initialState);
  const authenticatedUid = authState.user?.uid || null;
  const emailVerificationRequired = requiresEmailVerification(authState.user);
  const previousAuthenticatedUid = useRef(null);

  useAutosave(authState, dataState, dispatch);

  useEffect(() => {
    const guestSession = loadGuestJourneyState();
    const preview = guestPreviewPayload(guestSession, authenticatedUid && !emailVerificationRequired ? authenticatedUid : null);
    if (preview) {
      dispatch({ type: 'LOAD_GUEST_PREVIEW', payload: preview });
      if (authenticatedUid) previousAuthenticatedUid.current = authenticatedUid;
      return;
    }

    if (authenticatedUid) {
      previousAuthenticatedUid.current = authenticatedUid;
    }

    if (!authenticatedUid || emailVerificationRequired) {
      const suppressAnonymousLoad = !authenticatedUid && Boolean(previousAuthenticatedUid.current);
      dispatch({ type: 'CLEAR_DATA', payload: { suppressAnonymousLoad } });
    }
  }, [authenticatedUid, emailVerificationRequired]);

  useEffect(() => {
    if (!dataState.guestJourneyStatus || !dataState.guestAttemptId) return;
    const sessionState = loadGuestJourneyState();
    if (!sessionState || sessionState.attemptId !== dataState.guestAttemptId || sessionState.phase !== 'map') return;
    if (dataState.guestJourneyStatus === 'preview' && !dataState.userDocs?.steps?.length) return;
    saveGuestJourneyState({
      ...sessionState,
      boardData: normalizeJourneyData(dataState.userDocs),
      journey: { ...sessionState.journey, goal: dataState.activeGoal?.goal || sessionState.journey.goal, context: dataState.userDocs.context },
    });
  }, [dataState.activeGoal?.goal, dataState.guestAttemptId, dataState.guestJourneyStatus, dataState.userDocs]);

  useEffect(() => {
    const ownerUid = authenticatedUid && !emailVerificationRequired ? authenticatedUid : null;
    let cancelled = false;

    listLocalJourneys(ownerUid)
      .then((records) => {
        if (cancelled) return;
        dispatch({
          type: 'LOAD_LOCAL_GOALS',
          payload: {
            goals: records.map((record) => ({
              goal: record.title || 'Untitled journey',
              journeyId: record.id.slice(record.id.indexOf(':') + 1),
              source: record.source || 'local',
            })),
          },
        });
      })
      .catch((error) => console.log(`Local journey list unavailable: ${error?.message || 'unknown error'}`));

    return () => { cancelled = true; };
  }, [authenticatedUid, emailVerificationRequired]);

  useEffect(() => {
    if (!authenticatedUid || emailVerificationRequired) {
      return;
    }

    let cancelled = false;

    const loadJourneyBotJourneys = async () => {
      try {
        const result = await listJourneyBotJourneys();
        if (!cancelled) {
          dispatch({ type: 'LOAD_JOURNEYBOT_JOURNEYS', payload: result });
        }
      } catch (error) {
        console.log("JourneyBot journey list unavailable " + JSON.stringify(error));
        if (!cancelled) {
          dispatch({ type: 'JOURNEYBOT_JOURNEYS_UNAVAILABLE' });
          dispatch({ type: 'SHOW_ALERT', payload: { message: 'Could not load your cloud journeys. Try again.' } });
        }
      }
    };

    loadJourneyBotJourneys();

    return () => {
      cancelled = true;
    };
  }, [authenticatedUid, emailVerificationRequired]);

  // when do updates force a re-render
  // 
  // endpoints to do
  // () get journey for user only
  // send user id, assume that token is validated
  // send associated journey id  (if single user)
  // send associated journey location 
  // 

  // () get suggestions (this is where a shared model can help) for next steps
  // load suggestions as part of the tree but mark as suggsion in the payload 
  // { isSuggestion:'true'}


  // () save edits by user ()
  // single user, just send through assocaited user ID and save their notes to their document
  // 

  const saveToServer = async () => {
    console.log('DataProviderContext=============saveToServer START');
    // firebase save document to save the userDoc to firebase
    // use setDoc with ID
    // getDoc for each user on load id will be returned as part of user profile

    // find it under user-generated
    // use the key in authState.uid  to query in firebase
    // each user to an entire doc?  theres got to be a limit in place
    // ie to tell the user your plan is too complicated
    // keep it simple 

    // load the entire thing in userpad or userDAta session doc
    // rendering is up to the components


    console.log('DataProviderContext=============saveToServer END');



    // must return a list of stuff from firebase

    dispatch({ type: 'SAVE_DOC' });
  };

  const castVote = async () => {
    dispatch({ type: 'CAST_VOTE' });
  };



  // if we go single user mode
  // how do we switch to local group mode
  // how do we switch to bigger area mode

  // global app wide node id
  // how about local : user name + epoc time id

  const loadMyDocuments = async () => {
    if (!authState.user || requiresEmailVerification(authState.user)) {
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Not signed in. Cloud load is unavailable.' } });
      return;
    }

    try {
      const result = await loadJourneyWorkingCopy(authState.user.uid);
      if (!result.record) {
        dispatch({
          type: 'LOAD_WORKING_COPY',
          payload: {
            data: {},
            journeyMeta: null,
            storageStatus: result.status,
            cloudConflict: result.cloudRecord
              ? {
                localRecord: null,
                cloudRecord: result.cloudRecord,
              }
              : null,
          },
        });

        if (result.warning) {
          dispatch({ type: 'SHOW_ALERT', payload: { message: result.warning } });
        }
        return;
      }

      dispatch({
        type: 'LOAD_WORKING_COPY',
        payload: {
          data: result.record.data,
          journeyMeta: result.record,
          activeGoal: {
            goal: DEFAULT_GOAL_FOCUS,
            journeyId: 'default',
          },
          storageStatus: result.status,
          cloudConflict: result.cloudRecord
            ? {
              localRecord: result.record,
              cloudRecord: result.cloudRecord,
            }
            : null,
        },
      });

      if (result.warning) {
        dispatch({ type: 'SHOW_ALERT', payload: { message: result.warning } });
      }

    } catch (error) {
      console.log("Error occured here" + JSON.stringify(error));
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Cannot Load data.  Try again later' } });
    } finally {
      console.log("FINALLY===============: check dbstate value: ");
      console.log('result of dispatch VIEW' + dispatch({ type: 'VIEW' }));
      console.log('result of dispatch to data state' + JSON.stringify(dataState));
    }
    // must return a list of stuff from firebase
  };

  const selectGoalFocus = async (goal) => {
    const goalName = typeof goal === 'string' ? goal : goal?.goal;
    const journeyId = goal?.journeyId || goalToJourneyId(goalName);
    const apiJourneyId = goal?.apiJourneyId;

    if (!goalName || !journeyId) {
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Cannot load that goal yet.' } });
      return null;
    }

    if (dataState.activeGoal?.journeyId === journeyId && dataState.dataLoaded) {
      return dataState.journeyMeta || true;
    }

    try {
      if (dataState.journeyMeta?.id && journeyDataHasChanged(dataState.userDocs, dataState.journeyMeta.data)) {
        await saveJourneyLocal(dataState.journeyMeta, dataState.userDocs);
      }

      if (goal?.source === 'journeybot-api') {
        if (!authState.user || requiresEmailVerification(authState.user) || !apiJourneyId) {
          dispatch({ type: 'SHOW_ALERT', payload: { message: 'Sign in before loading that JourneyBot journey.' } });
          return null;
        }

        const record = await getJourneyBotRecord(apiJourneyId);
        const boardData = journeyBotRecordToBoardData(record);
        const savedRecord = await importJourneyBotRecordToLocal({
          userId: authState.user.uid,
          journeyId,
          data: boardData,
          title: record?.plan?.goal || goalName,
          cloudDocumentId: apiJourneyId,
        });

        dispatch({
          type: 'LOAD_GOAL_WORKING_COPY',
          payload: {
            data: savedRecord.data,
            journeyMeta: savedRecord,
            activeGoal: {
              goal: goalName,
              journeyId,
              apiJourneyId,
              source: 'journeybot-api',
              sourceProvider: goal.sourceProvider,
            },
            storageStatus: 'Imported from JourneyBot API',
          },
        });
        return savedRecord;
      }

      const result = await loadLocalGoalJourney({
        userId: authState.user?.uid || null,
        journeyId,
        goalName,
      });

      dispatch({
        type: 'LOAD_GOAL_WORKING_COPY',
        payload: {
          data: result.record?.data || { fromTheme: goalName, endTheme: goalName, steps: [] },
          journeyMeta: result.record,
          activeGoal: {
            goal: goalName,
            journeyId,
            source: goal?.source,
          },
          storageStatus: result.status,
        },
      });
      return result.record;
    } catch (error) {
      console.log("Goal switch failed " + JSON.stringify(error));
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Could not load that goal. Try again.' } });
      return null;
    }
  };

  const createGoalFocus = async (goalName) => {
    const title = String(goalName || '').trim();
    const validationErrors = getJourneyDetailsValidation({ goalFocus: title, context: '' });
    if (Object.keys(validationErrors).length > 0) {
      throw new Error(validationErrors.goalFocus || 'Enter a valid goal focus.');
    }
    if (authState.user && (dataState.lifeGoals?.goals || []).length >= SIGNED_IN_JOURNEY_LIMITS.goals) {
      throw new Error(`An account can contain up to ${SIGNED_IN_JOURNEY_LIMITS.goals} goals. Keeping three or fewer is recommended.`);
    }

    const baseId = makeJourneyId(title);
    const suffix = window.crypto?.randomUUID?.().slice(0, 8) || Date.now().toString(36);
    const journeyId = `${baseId.slice(0, 70)}-${suffix}`;

    try {
      if (dataState.journeyMeta?.id && journeyDataHasChanged(dataState.userDocs, dataState.journeyMeta.data)) {
        await saveJourneyLocal(dataState.journeyMeta, dataState.userDocs);
      }

      const result = await loadLocalGoalJourney({
        userId: authState.user?.uid || null,
        journeyId,
        goalName: title,
      });
      dispatch({
        type: 'LOAD_GOAL_WORKING_COPY',
        payload: {
          data: result.record.data,
          journeyMeta: result.record,
          activeGoal: { goal: title, journeyId, source: 'local' },
          storageStatus: 'Saved locally',
        },
      });
      return result.record;
    } catch (error) {
      dispatch({ type: 'SHOW_ALERT', payload: { message: error.message || 'Could not create that goal. Try again.' } });
      throw error;
    }
  };

  const deleteGoal = async (goal) => {
    const journeyId = goal?.journeyId;
    if (!journeyId || journeyId === 'default' || goal?.source === 'journeybot-api') {
      throw new Error('This goal cannot be deleted here.');
    }

    await deleteJourney({
      userId: authState.user?.uid || null,
      journeyId,
    });

    if (dataState.activeGoal?.journeyId !== journeyId) {
      dispatch({ type: 'DELETE_GOAL_COMPLETE', payload: { deletedJourneyId: journeyId } });
      return;
    }

    const fallback = await loadLocalGoalJourney({
      userId: authState.user?.uid || null,
      journeyId: 'default',
      goalName: DEFAULT_GOAL_FOCUS,
    });
    dispatch({
      type: 'DELETE_GOAL_COMPLETE',
      payload: {
        deletedJourneyId: journeyId,
        data: fallback.record.data,
        journeyMeta: fallback.record,
        activeGoal: { goal: DEFAULT_GOAL_FOCUS, journeyId: 'default' },
        storageStatus: fallback.status,
      },
    });
  };

  const resolveCloudChoice = async (choice) => {
    if (!authState.user || requiresEmailVerification(authState.user)) {
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Not signed in. Cloud load is unavailable.' } });
      return;
    }

    const conflict = dataState.cloudConflict;
    if (!conflict) {
      return;
    }

    try {
      let savedRecord;

      if (choice === 'cloud') {
        savedRecord = await chooseCloudJourneyVersion({
          userId: authState.user.uid,
          cloudRecord: conflict.cloudRecord,
        });
      } else if (choice === 'both') {
        savedRecord = await keepBothJourneyVersions({
          userId: authState.user.uid,
          localRecord: conflict.localRecord,
          cloudRecord: conflict.cloudRecord,
        });
      } else {
        savedRecord = await chooseLocalJourneyVersion({
          userId: authState.user.uid,
          localRecord: conflict.localRecord,
        });
      }

      dispatch({
        type: 'LOAD_WORKING_COPY',
        payload: {
          data: savedRecord.data,
          journeyMeta: savedRecord,
          storageStatus: choice === 'cloud' ? 'Saved to cloud' : 'Saved locally',
          cloudConflict: null,
        },
      });
    } catch (error) {
      console.log("Cloud choice failed " + JSON.stringify(error));
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Could not apply that cloud/local choice. Try again.' } });
    }
  };




  const saveTemplate = async () => {
    //  copy goals
    // copy group tasks
    //  ensure there are no items
    // 

  };


  // to autosave
  // i want to check if there has been any changes
  // if there has, then save
  // add a field that gets updated everytime the array changes
  // reset it every time a save is invoked
  // every time 
  // whats a good way to see if array has changed?

  // if there hasnt dont do anything


  const saveMyWork = async (someParam) => {
    console.log("START saveMyWork ======================================");
    // get user 

    // use setdoc with merge true to create if it does not exist and update if it does
    // const usersCollection = collection(db, '/user-generated-document');
    // const userId = '';  // get the user ID from the session
    // const cityRef = doc(db, '/user-generated-document', userId);
    //
    // Add a new document in collection "cities"
    // await setDoc(doc(db, "user-generated-document", userId), {
    //    graph: dataContext.getGraphData()
    //   shortname: 'Hard Coded Shortname',
    //    userId: userId
    // }, {merge:true});
    console.log('dataState.userPAD');
    console.log(dataState.userDocs);

    if (!authState.user || requiresEmailVerification(authState.user)) {
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Not signed in. Cloud save is unavailable.' } });
      return;
    }

    if (!dataState.journeyMeta) {
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Cannot save to cloud until the journey has loaded locally.' } });
      return;
    }

      try {
        const selectedJourneyId = dataState.activeGoal?.journeyId || 'default';
        const recordForSave = dataState.guestJourneyStatus
          ? { ...dataState.journeyMeta, id: `${authState.user.uid}:${selectedJourneyId}`, transient: false }
          : dataState.journeyMeta;
        const savedRecord = await saveJourneyCloud(
          authState.user.uid,
          recordForSave,
          dataState.userDocs,
          selectedJourneyId,
          dataState.activeGoal?.goal,
        );
        if (dataState.guestJourneyStatus) {
          clearGuestJourneyState();
          dispatch({ type: 'GUEST_FIRST_SAVE_COMPLETE', payload: { journeyMeta: savedRecord } });
        } else {
          dispatch({ type: 'CLOUD_SAVE_COMPLETE', payload: { journeyMeta: savedRecord } });
        }
      } catch (error) {
        console.log("Cloud save failed " + JSON.stringify(error));
        dispatch({ type: 'SHOW_ALERT', payload: { message: error.message || 'Cloud save failed. Check the Journey details and try again.' } });
      }


    console.log("END saveMyWork ======================================");
  };

  const saveLocalWork = async () => {
    if (!dataState.journeyMeta) {
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Cannot save locally until the journey has loaded.' } });
      return;
    }

    try {
      const savedRecord = await saveJourneyLocal(dataState.journeyMeta, dataState.userDocs);
      dispatch({
        type: 'LOCAL_SAVE_COMPLETE',
        payload: {
          journeyMeta: savedRecord,
          storageStatus: 'Saved locally',
        },
      });
    } catch (error) {
      console.log("Local save failed " + JSON.stringify(error));
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Local save failed. Try again before closing the browser.' } });
    }
  };

  const saveJourneyDetails = async ({ goalFocus, context }) => {
    const title = String(goalFocus || '').trim();
    const nextContext = String(context || '');
    const validationErrors = getJourneyDetailsValidation({ goalFocus: title, context: nextContext });

    if (Object.keys(validationErrors).length > 0) {
      throw new Error(validationErrors.goalFocus || validationErrors.context);
    }

    if (!dataState.journeyMeta) {
      const error = new Error('Cannot save journey details until the journey has loaded.');
      dispatch({ type: 'SHOW_ALERT', payload: { message: error.message } });
      throw error;
    }

    const nextDocs = normalizeJourneyData({ ...dataState.userDocs, context: nextContext });

    if (dataState.guestJourneyStatus) {
      dispatch({ type: 'UPDATE_GUEST_DETAILS', payload: { goalFocus: title, context: nextContext } });
      return { ...dataState.journeyMeta, title, data: nextDocs };
    }

    try {
      const savedRecord = await saveJourneyLocal(
        { ...dataState.journeyMeta, title },
        nextDocs,
      );
      dispatch({
        type: 'JOURNEY_DETAILS_SAVE_COMPLETE',
        payload: { journeyMeta: savedRecord, title },
      });
      return savedRecord;
    } catch (error) {
      dispatch({
        type: 'SHOW_ALERT',
        payload: { message: error.message || 'Local save failed. Try again before closing the browser.' },
      });
      throw error;
    }
  };

  const toggleActionItemStatus = async ({ listroot, actionItem }) => {
    const keyID = actionItem?.keyID;
    const currentStatus = actionItem?.status === 'completed' ? 'completed' : 'active';
    const nextStatus = currentStatus === 'completed' ? 'active' : 'completed';
    const previousDocs = dataState.userDocs;

    if (!keyID) {
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Could not update that action item.' } });
      return;
    }

    const payload = { listroot, keyID, status: nextStatus };
    const nextDocs = updateActionItemStatusInDocs(previousDocs, payload);

    dispatch({ type: 'SET_ACTION_ITEM_STATUS', payload });

    const apiJourneyId = dataState.activeGoal?.apiJourneyId || dataState.journeyMeta?.cloudDocumentId;
    const shouldPatchJourneyBotApi = Boolean(
      authState.user
      && !requiresEmailVerification(authState.user)
      && dataState.activeGoal?.source === 'journeybot-api'
      && apiJourneyId
      && actionItem.journeyBotActionId
    );

    try {
      if (shouldPatchJourneyBotApi) {
        await updateJourneyBotActionItem(apiJourneyId, actionItem.journeyBotActionId, {
          status: nextStatus,
        });
      }

      if (dataState.journeyMeta?.id && !dataState.guestJourneyStatus) {
        const savedRecord = await saveJourneyLocal(dataState.journeyMeta, nextDocs);
        dispatch({
          type: 'LOCAL_SAVE_COMPLETE',
          payload: {
            journeyMeta: savedRecord,
            storageStatus: shouldPatchJourneyBotApi ? 'Saved locally; JourneyBot action updated' : 'Saved locally',
          },
        });
      }
    } catch (error) {
      console.log("Action status update failed " + JSON.stringify(error));
      dispatch({ type: 'LOAD', payload: previousDocs });
      dispatch({
        type: 'SHOW_ALERT',
        payload: { message: error.message || 'Could not update that action item. Try again.' },
      });
      throw error;
    }
  };

  const exportJourneyJson = () => {
    const blob = new Blob([JSON.stringify(dataState.userDocs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${dataState.journeyMeta?.title || 'journey'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importJourneyJson = async (file) => {
    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const savedRecord = await importJourneyJsonToLocal({
        userId: authState.user?.uid || null,
        data: parsed,
      });
      dispatch({
        type: 'LOAD_WORKING_COPY',
        payload: {
          data: savedRecord.data,
          journeyMeta: savedRecord,
          storageStatus: 'Saved locally',
        },
      });
    } catch (error) {
      console.log("Import failed " + JSON.stringify(error));
      dispatch({ type: 'SHOW_ALERT', payload: { message: 'Import JSON failed. Check that the file contains a Journey JSON document.' } });
    }
  };

  const loadGuestPreview = (journey, sessionState = loadGuestJourneyState()) => {
    const nextSession = sessionState || saveGuestJourneyState({
      ...createGuestJourneyState(),
      phase: 'map',
      journey,
      boardData: guestJourneyToBoardData(journey),
    });
    const payload = guestPreviewPayload(nextSession, authState.user?.uid || null);
    if (payload) dispatch({ type: 'LOAD_GUEST_PREVIEW', payload });
  };

  const startOverGuestJourney = () => {
    clearGuestJourneyState();
    const next = saveGuestJourneyState(createGuestJourneyState());
    dispatch({ type: 'CLEAR_DATA', payload: { suppressAnonymousLoad: true } });
    return next;
  };



  // this is the component entry point to the reducer
  // this doesnt feel right.  its working now but should be changed later

  const updateClientSideJourney = (stepId, userEntry, keyId, action) => {
    console.log('Update Document Called');
    console.log('current intiitla state' + JSON.stringify(initialState));
    console.log('current state: USER PAD' + JSON.stringify(initialState.userDocs));
    const tempJourney = { step_Id: 0, index: 0, value: userEntry };
    console.log("Update client side :\n" + JSON.stringify(tempJourney));
    dispatch({ type: 'TAKE_SUGGESTION', payload: tempJourney });
  };

  const removeClientSideItem = ({ stepId, userEntry, keyId, action }) => {
    console.log('removeClientSideItem Document Called');
    console.log(keyId);
    dispatch({ type: 'REMOVE_SUGGESTION', payload: keyId });
  };



  return (
    <DataContext.Provider value={{
      dataState,
      updateClientSideJourney,
      removeClientSideItem,
      saveLocalWork,
      saveJourneyDetails,
      saveMyWork,
      loadMyDocuments,
      selectGoalFocus,
      createGoalFocus,
      deleteGoal,
      resolveCloudChoice,
      exportJourneyJson,
      importJourneyJson,
      loadGuestPreview,
      startOverGuestJourney,
      toggleActionItemStatus,
      dispatch
    }}>
      {children}
    </DataContext.Provider>
  );







};

export const useData = () => {
  return React.useContext(DataContext);
};


